import cx from "classnames";
import React, { useEffect, useRef } from "react";

import useClickOutside from "@/mk2/hooks/useClickOutside";
import useEventCallback from "@/mk2/hooks/useEventCallback";
import {
    BZ2_FILTER_MODAL_CLOSE,
    BZ2_FILTER_PRICE_MAX,
    BZ2_FILTER_PRICE_MIN,
} from "@/mk/autogenerated/translations/PriceFacetDropdown.b6d8c07c7075ad16b3aa343665461893";

import styles from "./PriceFacetDropdown.mscss";

export type PriceType = "min" | "max";

export interface OwnProps {
    className?: string;
    min?: number;
    max?: number;

    onChange(type: PriceType, value: number);
    onClose();
}

type Props = OwnProps;

const PriceFacetDropdown = React.memo<Props>(({ className, min, max, onChange, onClose }) => {
    const inputRef = useRef<HTMLInputElement>();
    const containerRef = useRef<HTMLDivElement>();

    const onContainerClick = useEventCallback((event: React.MouseEvent<HTMLDivElement>) => {
        event.stopPropagation();
    }, []);

    const onMinChange = useEventCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            onChange(
                "min",
                event.currentTarget.value ? parseFloat(event.currentTarget.value) : null,
            );
        },
        [onChange],
    );

    const onMaxChange = useEventCallback(
        (event: React.FormEvent<HTMLInputElement>) => {
            onChange(
                "max",
                event.currentTarget.value ? parseFloat(event.currentTarget.value) : null,
            );
        },
        [onChange],
    );

    // Focus search on mount
    useEffect(() => {
        inputRef.current.focus();
    }, []);

    // Click outside
    useClickOutside(containerRef, onClose);

    return (
        <div
            className={cx(styles.PriceFacetDropdown, className)}
            onClick={onContainerClick}
            ref={containerRef}
        >
            <div className={styles.PriceFacetDropdown__content}>
                <div className={styles.PriceFacetDropdown__header}>
                    <div className={styles.PriceFacetDropdown__header__dismiss}>
                        <button
                            className={styles.PriceFacetDropdown__header__dismiss__button}
                            type="button"
                            onClick={onClose}
                        >
                            {BZ2_FILTER_MODAL_CLOSE}
                        </button>
                    </div>
                </div>
                <div className={styles.PriceFacetDropdown__fields}>
                    <div className={styles.PriceFacetDropdown__field}>
                        <input
                            className={styles.PriceFacetDropdown__field__input}
                            type="number"
                            ref={inputRef}
                            name="min"
                            value={min === 0 ? "0" : min || ""}
                            max={max || undefined}
                            min="0"
                            placeholder={BZ2_FILTER_PRICE_MIN}
                            onChange={onMinChange}
                        />
                    </div>
                    <div className={styles.PriceFacetDropdown__field}>
                        <input
                            className={styles.PriceFacetDropdown__field__input}
                            type="number"
                            name="max"
                            value={max === 0 ? "0" : max || ""}
                            min={min || "0"}
                            placeholder={BZ2_FILTER_PRICE_MAX}
                            onChange={onMaxChange}
                        />
                    </div>
                </div>
            </div>
        </div>
    );
});

PriceFacetDropdown.displayName = "PriceFacetDropdown";

export default PriceFacetDropdown;
